// ============================================
// SITE OF SERVICE — LEVERAGE CLASSIFIER
// Rules-based: no AI call required, runs instantly
// Supports PROACTIVE and SALVAGE variants
// ============================================

import type {
    SiteOfServiceInputs,
    LeverageClassification,
    LeverageLevel,
    SoSDimension,
  } from "../types/siteOfService";
  
  /** Words/phrases indicating concrete clinical specifics (not vague preference) */
  const CONCRETE_INDICATORS = [
    // Rescue
    "return to or", "re-explore", "salvage", "immediate intervention",
    "bedside", "on-site", "on site", "minutes", "within hour",
    "emergent", "tamponade", "hemorrhage", "airway",
    // Monitoring
    "hourly", "q1h", "q2h", "continuous", "telemetry", "icu",
    "step-down", "neuro checks", "neurovascular", "vitals every",
    "overnight observation", "24-hour", "24 hour",
    // Timing
    "hours", "days", "week", "delay", "progression", "ischemia",
    "infection", "sepsis", "necrosis", "infarct", "perforation",
    "rupture", "compression",
    // Coupling
    "same anesthetic", "single anesthetic", "combined", "intraoperative",
    "frozen section", "sentinel node", "coordinated", "staged",
    // Team
    "protocol", "pathway", "team", "on-call", "coverage",
    "nursing ratio", "staffing", "specializ",
    // Patient vulnerability
    "bmi", "cardiac", "pulmonary", "renal", "anticoagul",
    "dialysis", "immunocompromised", "transplant", "pediatric",
    "geriatric", "frail", "asa class", "asa 3", "asa 4",
    // Irreversible
    "irreversible", "permanent", "loss of", "amputation",
    "paralysis", "blindness", "infarction", "death", "mortality",
    "organ loss", "bowel necrosis", "limb", "fertility",
  ];
  
  /** Salvage-specific: words indicating clinical deterioration during delay */
  const DETERIORATION_INDICATORS = [
    // Reconstruction-specific
    "expander", "capsular contracture", "tissue quality", "flap",
    "radiation window", "reconstruction window", "candidacy",
    "skin envelope", "fibrosis",
    // General deterioration
    "progression", "worsening", "deteriorat", "infection",
    "wound", "dehiscence", "necrosis", "sepsis",
    "contracture", "stiffness", "atrophy",
    // Oncology timing
    "disease progression", "staging", "metasta", "recurrence",
    "treatment window", "chemo delay", "radiation delay",
    // Psychological / functional
    "psychological", "functional decline", "quality of life",
    "pain", "disability", "immobil",
  ];
  
  /** Vague preference words that don't add leverage */
  const VAGUE_INDICATORS = [
    "comfortable", "prefer", "familiar", "used to",
    "convenient", "closer", "reputation", "always done",
    "my patients", "continuity",
  ];
  
  function hasConcreteSpecifics(text: string): boolean {
    if (!text || text.trim().length < 10) return false;
    const lower = text.toLowerCase();
    return CONCRETE_INDICATORS.some((indicator) => lower.includes(indicator));
  }
  
  function hasDeteriorationSpecifics(text: string): boolean {
    if (!text || text.trim().length < 10) return false;
    const lower = text.toLowerCase();
    return DETERIORATION_INDICATORS.some((indicator) => lower.includes(indicator));
  }
  
  function isVagueOnly(text: string): boolean {
    if (!text || text.trim().length < 5) return false;
    const lower = text.toLowerCase();
    const hasVague = VAGUE_INDICATORS.some((v) => lower.includes(v));
    const hasConcrete = hasConcreteSpecifics(text);
    return hasVague && !hasConcrete;
  }
  
  function isUnknown(text: string): boolean {
    if (!text) return true;
    const lower = text.trim().toLowerCase();
    return (
      lower === "" ||
      lower === "unknown" ||
      lower === "not sure" ||
      lower === "n/a" ||
      lower === "na"
    );
  }
  
  function isFilled(text: string): boolean {
    return !!text && text.trim().length > 0 && !isUnknown(text);
  }
  
  /** Parse priorAttempts string to a number */
  function parseAttempts(text: string): number {
    if (!text) return 0;
    const clean = text.trim().replace("+", "");
    const n = parseInt(clean, 10);
    return isNaN(n) ? 0 : n;
  }
  
  // ============================================
  // PROACTIVE CLASSIFIER (unchanged)
  // ============================================
  
  function classifyProactive(inputs: SiteOfServiceInputs): LeverageClassification {
    const factors: SoSDimension[] = [];
    const rationale: string[] = [];
  
    // 1. Rescue window
    if (isFilled(inputs.rescueWindow) && hasConcreteSpecifics(inputs.rescueWindow)) {
      factors.push("rescue_window");
      rationale.push("concrete rescue capability difference identified");
    }
  
    // 2. Monitoring differential
    const monPrefFilled = isFilled(inputs.monitoringPreferred);
    const monRedUnknown = isUnknown(inputs.monitoringRedirected);
    const monRedFilled = isFilled(inputs.monitoringRedirected) && !monRedUnknown;
  
    if (monPrefFilled && hasConcreteSpecifics(inputs.monitoringPreferred)) {
      if (monRedUnknown) {
        factors.push("monitoring_differential");
        rationale.push("specific preferred-site monitoring described; redirected-site monitoring unknown — generates clarification questions");
      } else if (monRedFilled) {
        factors.push("monitoring_differential");
        rationale.push("monitoring frequency/expertise differential between sites");
      }
    }
  
    // 3. Timing delay
    if (isFilled(inputs.timingSensitivity) && hasConcreteSpecifics(inputs.timingSensitivity)) {
      factors.push("timing_delay");
      rationale.push("delay introduces defined clinical risk window");
    }
  
    // 4. Procedure coupling
    if (isFilled(inputs.procedureCoupling) && hasConcreteSpecifics(inputs.procedureCoupling)) {
      factors.push("procedure_coupling");
      rationale.push("separation into multiple anesthetics increases risk");
    }
  
    // 5. Team/protocol dependency
    if (isFilled(inputs.teamProtocol) && hasConcreteSpecifics(inputs.teamProtocol)) {
      factors.push("team_protocol");
      rationale.push("site-specific protocol affects safety or outcomes");
    }
  
    // 6+7. Patient vulnerability + irreversible consequence
    if (isFilled(inputs.patientVulnerability)) {
      if (hasConcreteSpecifics(inputs.patientVulnerability)) {
        factors.push("patient_vulnerability");
        factors.push("irreversible_consequence");
        rationale.push("patient-specific vulnerability with identifiable irreversible risk");
      } else if (!isVagueOnly(inputs.patientVulnerability)) {
        factors.push("patient_vulnerability");
        rationale.push("patient-specific factor noted but specificity is moderate");
      }
    }
  
    // Classification rules
    let level: LeverageLevel;
  
    if (factors.length >= 2 && factors.some((f) =>
      f === "rescue_window" || f === "monitoring_differential" || f === "timing_delay" || f === "irreversible_consequence"
    )) {
      level = "HIGH";
    } else if (factors.length >= 1) {
      const strongFactors: SoSDimension[] = [
        "rescue_window", "timing_delay", "irreversible_consequence",
      ];
      if (factors.length === 1 && strongFactors.includes(factors[0]) && hasConcreteSpecifics(
        factors[0] === "rescue_window" ? inputs.rescueWindow :
        factors[0] === "timing_delay" ? inputs.timingSensitivity :
        inputs.patientVulnerability
      )) {
        level = "HIGH";
      } else {
        level = "MODERATE";
      }
    } else {
      level = "LOW";
    }
  
    return {
      level,
      rationale: rationale.length > 0
        ? rationale.slice(0, 2).join("; ")
        : "No specific care-pathway difference identified — may be primarily contractual",
      contributingFactors: factors,
    };
  }
  
  // ============================================
  // SALVAGE CLASSIFIER
  // ============================================
  
  function classifySalvage(inputs: SiteOfServiceInputs): LeverageClassification {
    const factors: SoSDimension[] = [];
    const rationale: string[] = [];
    const attempts = parseAttempts(inputs.priorAttempts);
  
    // 1. Documented failure pattern
    if (attempts >= 1 && isFilled(inputs.failureReasons)) {
      factors.push("documented_failure");
      if (attempts >= 3) {
        rationale.push(`${attempts} documented cancellations at approved facility`);
      } else {
        rationale.push(`${attempts} cancellation(s) at approved facility`);
      }
    }
  
    // 2. Elapsed delay
    if (isFilled(inputs.elapsedDelay)) {
      factors.push("elapsed_delay");
      rationale.push(`elapsed delay: ${inputs.elapsedDelay.trim()}`);
    }
  
    // 3. Clinical deterioration (the critical dimension)
    if (isFilled(inputs.clinicalDeterioration)) {
      if (hasDeteriorationSpecifics(inputs.clinicalDeterioration)) {
        factors.push("clinical_deterioration");
        rationale.push("specific clinical deterioration during delay");
      } else if (!isVagueOnly(inputs.clinicalDeterioration)) {
        factors.push("clinical_deterioration");
        rationale.push("clinical change noted but specificity is moderate");
      }
    }
  
    // 4. Alternative facility availability
    if (isFilled(inputs.alternativeSiteCapability)) {
      factors.push("alternative_available");
      rationale.push("alternative facility with documented capability available");
    }
  
    // 5. Shared: rescue window (if preferred site has capabilities)
    if (isFilled(inputs.rescueWindow) && hasConcreteSpecifics(inputs.rescueWindow)) {
      factors.push("rescue_window");
      rationale.push("rescue capability difference supports alternative site");
    }
  
    // 6. Shared: monitoring differential
    if (isFilled(inputs.monitoringPreferred) && hasConcreteSpecifics(inputs.monitoringPreferred)) {
      factors.push("monitoring_differential");
    }
  
    // 7. Shared: patient vulnerability
    if (isFilled(inputs.patientVulnerability) && hasConcreteSpecifics(inputs.patientVulnerability)) {
      factors.push("patient_vulnerability");
      factors.push("irreversible_consequence");
    }
  
    // Classification rules — salvage is generally stronger than proactive
    let level: LeverageLevel;
  
    const hasFailure = factors.includes("documented_failure");
    const hasDeterioration = factors.includes("clinical_deterioration");
    const hasDelay = factors.includes("elapsed_delay");
    const hasAlternative = factors.includes("alternative_available");
  
    if (hasFailure && hasDeterioration && (attempts >= 2 || hasDelay)) {
      // Multiple cancellations + clinical deterioration = strongest position
      level = "HIGH";
    } else if (hasFailure && (hasDeterioration || (hasDelay && hasAlternative))) {
      // Documented failure + either deterioration or delay+alternative
      level = "HIGH";
    } else if (hasFailure && hasDelay) {
      // Documented failure + delay but no specific deterioration
      level = "MODERATE";
    } else if (factors.length >= 2) {
      level = "MODERATE";
    } else {
      level = "LOW";
    }
  
    return {
      level,
      rationale: rationale.length > 0
        ? rationale.slice(0, 3).join("; ")
        : "Insufficient documentation of access failure",
      contributingFactors: factors,
    };
  }
  
  // ============================================
  // PUBLIC API
  // ============================================
  
  export function classifySiteOfServiceLeverage(
    inputs: SiteOfServiceInputs
  ): LeverageClassification {
    if (inputs.variant === "SALVAGE") {
      return classifySalvage(inputs);
    }
    return classifyProactive(inputs);
  }
  
  /** Completeness helper — adapts field count to variant */
  export function getSoSCompleteness(inputs: SiteOfServiceInputs) {
    if (inputs.variant === "SALVAGE") {
      const fields: Partial<Record<keyof SiteOfServiceInputs, boolean>> = {
        // Shared (4)
        rescueWindow: isFilled(inputs.rescueWindow),
        monitoringPreferred: isFilled(inputs.monitoringPreferred),
        monitoringRedirected: isFilled(inputs.monitoringRedirected) || isUnknown(inputs.monitoringRedirected),
        patientVulnerability: isFilled(inputs.patientVulnerability),
        // Salvage-specific (5)
        priorAttempts: isFilled(inputs.priorAttempts),
        failureReasons: isFilled(inputs.failureReasons),
        elapsedDelay: isFilled(inputs.elapsedDelay),
        clinicalDeterioration: isFilled(inputs.clinicalDeterioration),
        alternativeSiteCapability: isFilled(inputs.alternativeSiteCapability),
      };
      const filled = Object.values(fields).filter(Boolean).length;
      return { filled, total: 9, fields };
    }
  
    // Proactive (default)
    const fields: Partial<Record<keyof SiteOfServiceInputs, boolean>> = {
      rescueWindow: isFilled(inputs.rescueWindow),
      monitoringPreferred: isFilled(inputs.monitoringPreferred),
      monitoringRedirected: isFilled(inputs.monitoringRedirected) || isUnknown(inputs.monitoringRedirected),
      timingSensitivity: isFilled(inputs.timingSensitivity),
      procedureCoupling: isFilled(inputs.procedureCoupling),
      teamProtocol: isFilled(inputs.teamProtocol),
      patientVulnerability: isFilled(inputs.patientVulnerability),
    };
    const filled = Object.values(fields).filter(Boolean).length;
    return { filled, total: 7, fields };
  }